import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Search,
  Send,
  Clock,
  Pause,
  Play,
  XCircle,
  Copy,
  Trash2,
  Edit2,
  Eye,
  BarChart3,
  Users,
  Mail,
  CheckCircle,
  ArrowLeft,
  Megaphone,
  Calendar,
  MousePointerClick,
  ChevronDown,
  Loader2,
  Server,
  UtensilsCrossed,
  Music,
  ClockIcon,
  Truck,
  Tag,
  PartyPopper,
  FileText,
  Sparkles,
  Wand2,
  AlertTriangle,
  FlaskConical,
  UserX,
  Info,
  ClipboardCopy,
} from "lucide-react";
import api from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "@/contexts/LocationContext";
import { useSubscription } from "@/hooks/useSubscription";
import { usePermissions } from "@/hooks/usePermissions";
import UpgradeRequired from "@/components/UpgradeRequired";
import EmailBlockEditor, {
  buildBlocksFragment,
  type EmailBlock,
} from "@/components/EmailBlockEditor";
import EmailHtmlSizeNotice from "@/components/EmailHtmlSizeNotice";
import { wrapEmailContent, BRAND_COLORS } from "@/lib/email-layout";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import CampaignRecipientsTable, {
  type CampaignStatsRecipient,
  type RecipientStatusFilter,
} from "@/components/CampaignRecipientsTable";

/**
 * Mensaje de error que devuelve la API, en vez del genérico de axios
 * ("Request failed with status code 400"), que no le dice nada al usuario.
 */
function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as { response?: { data?: { error?: string; message?: string } } }
    ).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return fallback;
}

/**
 * Convierte un ISO UTC (el que devuelve el backend en `scheduledAt`) al valor
 * que espera un `<input type="datetime-local">`, en la zona horaria LOCAL del
 * navegador. El propio input siempre interpreta/genera su valor en hora
 * local (nunca UTC): recortar el ISO en crudo (`iso.slice(0, 16)`) muestra la
 * hora UTC como si fuera local, y al guardar de nuevo (`new
 * Date(value).toISOString()`, que SÍ interpreta el valor como local) la
 * campaña se reprogramaba varias horas antes de lo previsto (la diferencia
 * exacta del huso horario local, dos veces: una al mostrar, otra al guardar).
 */
function isoToLocalDatetimeInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ============================================================================
// TYPES
// ============================================================================

type ConsentMode = "strict" | "soft";

interface Campaign {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  audienceType: string;
  audienceFilters: AudienceFilters | null;
  status: string;
  consentMode: ConsentMode;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  openCount: number;
  clickCount: number;
  createdAt: string;
  _count?: { recipients: number };
}

interface AudienceFilters {
  tags?: string[];
  minReservations?: number;
  lastVisitDays?: number;
  onlyWithEmail?: boolean;
  customEmails?: string[];
  /** Solo para audienceType "reservation_window": rango de fechas "YYYY-MM-DD" (inclusive). */
  reservationFrom?: string;
  reservationTo?: string;
}

interface AudiencePreview {
  total: number;
  withEmail: number;
  withoutEmail: number;
  unsubscribed: number;
  bounced: number;
  withConsent: number;
  eligibleStrict: number;
  eligibleSoft: number;
  consentMode: ConsentMode;
  eligible: number;
  preview: Array<{
    id: string;
    name: string;
    email: string | null;
    marketingConsent: boolean;
    eligible: boolean;
  }>;
}

/** Desglose de un segmento de audiencia (contrato de GET /email-campaigns/audience-stats) */
interface AudienceSegmentStats {
  total: number;
  withEmail: number;
  withoutEmail: number;
  unsubscribed: number;
  bounced: number;
  withConsent: number;
  eligibleStrict: number;
  eligibleSoft: number;
  /**
   * true si este desglose viene del fallback de compatibilidad (el backend
   * no envió "segments"): en ese caso withoutEmail/unsubscribed/bounced son
   * desconocidos (no ceros reales) y eligibleSoft no es fiable.
   */
  degraded?: boolean;
}

interface AudienceStatsResponse {
  lastVisitDays: number;
  segments?: {
    all: AudienceSegmentStats;
    recent: AudienceSegmentStats;
  };
  // Compatibilidad hacia atrás con backends que no envíen "segments"
  all: number;
  allEligible: number;
  recent: number;
  recentEligible: number;
}

/**
 * Devuelve el desglose de un segmento. Si el backend no envía el contrato
 * nuevo (`segments`), construye un desglose defensivo a partir de los
 * campos planos de compatibilidad (sin datos de bajas/rebotes).
 */
function getSegmentStats(
  stats: AudienceStatsResponse | null,
  segment: "all" | "recent",
): AudienceSegmentStats | null {
  if (!stats) return null;
  if (stats.segments) return stats.segments[segment];
  const total = segment === "all" ? stats.all : stats.recent;
  const eligible = segment === "all" ? stats.allEligible : stats.recentEligible;
  return {
    total,
    withEmail: total,
    withoutEmail: 0,
    unsubscribed: 0,
    bounced: 0,
    withConsent: eligible,
    eligibleStrict: eligible,
    eligibleSoft: total,
    degraded: true,
  };
}

interface CampaignStats {
  campaign: Campaign;
  byStatus: Record<string, number>;
  openRate: number;
  clickRate: number;
  /** Bajas atribuidas a ESTA campaña (recipients con `unsubscribedAt != null`). */
  unsubscribedCount: number;
  /**
   * Reservas atribuidas a ESTA campaña (clic en el botón del email → widget →
   * reserva). Es un suelo, no el total: la reserva por teléfono no se atribuye.
   */
  reservedCount: number;
  /** Página actual de la tabla de destinatarios (paginada, con búsqueda/filtro). */
  recipients: CampaignStatsRecipient[];
  /** Total de destinatarios que casan el filtro/búsqueda (para paginar). */
  total: number;
  page: number;
  limit: number;
}

/** Tamaño de página por defecto de la tabla de destinatarios (igual que el backend). */
const CAMPAIGN_STATS_DEFAULT_LIMIT = 25;

type ViewMode = "list" | "editor" | "stats" | "templates" | "smtp";

/**
 * Placeholders que `send-service.ts` sustituye DE VERDAD al enviar (fuente
 * de la verdad: `CAMPAIGN_PLACEHOLDERS` / `buildCampaignEmailHtml` del
 * backend). Todos funcionan en el CONTENIDO; en el ASUNTO solo
 * `{{customerName}}` y `{{email}}` (`personalizeCampaignSubject`). Si el
 * backend amplía la lista, añadir aquí (y solo aquí: esta tabla es la única
 * fuente de la guía visual).
 */
const CAMPAIGN_VARIABLES: Array<{
  token: string;
  description: string;
  highlight?: boolean;
}> = [
  {
    token: "{{customerName}}",
    description:
      'Nombre del cliente al que se envía («María»). Funciona también en el asunto',
  },
  {
    token: "{{email}}",
    description:
      "Email del cliente al que se envía este correo. Funciona también en el asunto",
  },
  {
    token: "{{bookingUrl}}",
    description: "Enlace a tu página de reservas (widget)",
  },
  {
    token: "{{reservationUrl}}",
    description:
      'Enlace a LA RESERVA CONCRETA del cliente. En campañas con audiencia "Con reserva entre fechas" abre su reserva de esa franja para ver o cambiar el menú; en el resto de campañas equivale a {{bookingUrl}}',
    highlight: true,
  },
  {
    token: "{{unsubscribeUrl}}",
    description:
      "Página donde el cliente gestiona sus preferencias de email o se da de baja",
  },
  {
    token: "{{quickUnsubscribeUrl}}",
    description:
      "Baja instantánea en 1 clic. Si no incluyes ninguno de los dos enlaces de baja, se añade un pie automático con él",
  },
];

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; icon: typeof Clock }
> = {
  draft: {
    label: "Borrador",
    bg: "bg-gray-100",
    text: "text-gray-700",
    icon: Edit2,
  },
  scheduled: {
    label: "Programada",
    bg: "bg-blue-100",
    text: "text-blue-700",
    icon: Clock,
  },
  sending: {
    label: "Enviando",
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: Send,
  },
  sent: {
    label: "Enviada",
    bg: "bg-green-100",
    text: "text-green-700",
    icon: CheckCircle,
  },
  paused: {
    label: "Pausada",
    bg: "bg-orange-100",
    text: "text-orange-700",
    icon: Pause,
  },
  cancelled: {
    label: "Cancelada",
    bg: "bg-red-100",
    text: "text-red-700",
    icon: XCircle,
  },
};

const CAMPAIGN_TEMPLATES = [
  {
    id: "blank",
    name: "En blanco",
    description: "Empieza desde cero con una plantilla básica",
    icon: FileText,
    color: "bg-gray-100 text-gray-600",
    subject: "",
    html: "blank",
  },
  {
    id: "menu_especial",
    name: "Menú Especial",
    description: "Presenta un nuevo menú, menú degustación o menú de temporada",
    icon: UtensilsCrossed,
    color: "bg-amber-100 text-amber-700",
    subject: "🍽️ Nuevo menú especial en {{restaurantName}}",
    html: "menu_especial",
  },
  {
    id: "evento",
    name: "Evento / Espectáculo",
    description: "Anuncia un evento, actuación en vivo o noche temática",
    icon: Music,
    color: "bg-purple-100 text-purple-700",
    subject: "🎶 Evento especial en {{restaurantName}}",
    html: "evento",
  },
  {
    id: "horarios",
    name: "Horarios de Apertura",
    description: "Informa sobre horarios, cambios de horario o festivos",
    icon: ClockIcon,
    color: "bg-blue-100 text-blue-700",
    subject: "🕐 Nuevos horarios de {{restaurantName}}",
    html: "horarios",
  },
  {
    id: "delivery",
    name: "Servicio de Delivery",
    description: "Promociona tu servicio de comida a domicilio o take away",
    icon: Truck,
    color: "bg-green-100 text-green-700",
    subject: "🚗 ¡Pedidos a domicilio en {{restaurantName}}!",
    html: "delivery",
  },
  {
    id: "promocion",
    name: "Promoción / Oferta",
    description: "Comunica descuentos, ofertas especiales o happy hours",
    icon: Tag,
    color: "bg-red-100 text-red-700",
    subject: "🎉 Oferta especial en {{restaurantName}}",
    html: "promocion",
  },
  {
    id: "festivo",
    name: "Celebración / Festivo",
    description: "Felicita por Navidad, San Valentín, día de la madre, etc.",
    icon: PartyPopper,
    color: "bg-pink-100 text-pink-700",
    subject: "🎊 ¡Celebra con nosotros en {{restaurantName}}!",
    html: "festivo",
  },
  {
    id: "novedades",
    name: "Novedades / Newsletter",
    description: "Comparte noticias, cambios o novedades del restaurante",
    icon: Sparkles,
    color: "bg-indigo-100 text-indigo-700",
    subject: "✨ Novedades de {{restaurantName}}",
    html: "novedades",
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export default function EmailCampaigns() {
  const { user } = useAuth();
  const { currentLocation, locations } = useLocation();

  // Local al que llevará el botón de reserva de la campaña.
  //
  // OJO: NO es el local del selector del dashboard. Una campaña es de la
  // ORGANIZACIÓN (el modelo `EmailCampaign` no tiene `locationId`), así que el
  // envío no sabe qué local estaba viendo el usuario: el backend resuelve
  // `{{bookingUrl}}` con el primer local ACTIVO por `createdAt` ascendente —el
  // más antiguo, en la práctica el principal— (ver `resolveCampaignBookingUrl`
  // en reservamanager-backend/src/modules/email-campaigns/controller.ts). Aquí
  // se replica ESA MISMA regla para que la vista previa no enseñe un local
  // distinto del que recibirán los comensales. Si cambia una, cambiar la otra.
  //
  // Se acota a la organización del local seleccionado porque el superadmin
  // recibe en `locations` los locales de TODAS las organizaciones.
  const bookingLocation = useMemo(() => {
    if (!currentLocation) return null;
    const organizationId = currentLocation.organizationId;
    return (
      [...locations]
        .filter(
          (l) =>
            l.isActive &&
            !!l.slug?.trim() &&
            (!organizationId || l.organizationId === organizationId),
        )
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0] ?? null
    );
  }, [locations, currentLocation]);

  // Nombre del NEGOCIO que se pinta en la cabecera/pie del email (plantillas
  // y generador de IA) y que sustituye a `{{restaurantName}}` en el asunto.
  //
  // OJO: `user?.organization` es SIEMPRE la organización propia del token
  // (la del superadmin), nunca la organización EFECTIVA cuando un superadmin
  // está impersonando un cliente vía el selector de local (`X-Organization-Id`,
  // ver `LocationContext`). Mismo criterio ya usado ahí para
  // `currentBookingMode`: para superadmin, la organización del
  // `currentLocation` seleccionado; para el resto, la propia del usuario.
  const isSuperAdmin = user?.role === "owner";
  const brandName =
    (isSuperAdmin
      ? currentLocation?.organization?.name
      : user?.organization?.name) || "Tu negocio";

  // Subscription check
  const {
    features,
    isLoading: subscriptionLoading,
    canAccessEmailCampaigns,
  } = useSubscription();

  // Gating por permisos (FASE 3c): el backend restringe TODAS las mutaciones
  // de campañas (crear, editar, enviar, enviar prueba, pausar, reanudar,
  // cancelar, duplicar, eliminar, generar con IA) al permiso configurable
  // "campanas:gestionar" (owner/admin siempre pasan vía `usePermissions`,
  // staff según su configuración; ver `requireCampaigns` en
  // reservamanager-backend/src/modules/email-campaigns/routes.ts). Se
  // replica aquí para ocultar los controles de gestión proactivamente; el
  // 403 del backend queda como red de seguridad si algo se cuela. La
  // lectura (listado y estadísticas) sigue abierta a todo el equipo.
  const { can } = usePermissions();
  const canManageCampaigns = can("campanas:gestionar");

  // SMTP state
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [smtpFromName, setSmtpFromName] = useState("");
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpMessage, setSmtpMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpChecked, setSmtpChecked] = useState(false);

  // View state
  const [view, setView] = useState<ViewMode>("list");
  const [editingId, setEditingId] = useState<string | null>(null);

  // List state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  // Editor state
  const [form, setForm] = useState({
    name: "",
    subject: "",
    htmlContent: "",
    audienceType: "all",
    audienceFilters: {} as AudienceFilters,
    scheduledAt: "",
    consentMode: "strict" as ConsentMode,
  });
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [contentTab, setContentTab] = useState<"visual" | "html">("html");
  const [audiencePreview, setAudiencePreview] =
    useState<AudiencePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [customEmailsText, setCustomEmailsText] = useState("");
  const [audienceStats, setAudienceStats] =
    useState<AudienceStatsResponse | null>(null);

  // Stats state
  const [stats, setStats] = useState<CampaignStats | null>(null);
  // Campaña cuyas estadísticas se están viendo (distinto de `stats?.campaign.id`
  // porque tiene que existir ANTES de la primera respuesta, para poder disparar
  // el efecto de refetch en cuanto se entra en la pestaña).
  const [statsCampaignId, setStatsCampaignId] = useState<string | null>(null);
  // Tabla paginada de destinatarios: página/tamaño/búsqueda/filtro de la
  // pestaña de estadísticas. `recipientsSearchInput` es el valor tal cual del
  // buscador; `recipientsSearch` es la versión debounced que de verdad viaja
  // a la API (evita una petición por tecla).
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsLimit, setRecipientsLimit] = useState(
    CAMPAIGN_STATS_DEFAULT_LIMIT,
  );
  const [recipientsSearchInput, setRecipientsSearchInput] = useState("");
  const [recipientsSearch, setRecipientsSearch] = useState("");
  const [recipientsStatusFilter, setRecipientsStatusFilter] =
    useState<RecipientStatusFilter>("all");
  // Refetch en curso (carga inicial de la pestaña Y paginación/búsqueda/
  // filtro posteriores). Solo pinta un indicador discreto junto al título de
  // la tabla; la pantalla completa se gatea con `!stats` (ver `renderStats`).
  const [recipientsTableLoading, setRecipientsTableLoading] = useState(false);

  // AI Generator
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);

  // Guía de variables ({{customerName}}, {{reservationUrl}}...), plegada por
  // defecto: la tabla completa ocupa bastante y la mayoría de sesiones de
  // edición no la necesitan abierta todo el rato.
  const [showVariablesGuide, setShowVariablesGuide] = useState(false);

  // Test email
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // General
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Check SMTP configuration
  useEffect(() => {
    if (user?.role === "owner") {
      setSmtpConfigured(true);
      setSmtpChecked(true);
      return;
    }
    const checkSmtp = async () => {
      try {
        const res = await api.get("/organizations/me/smtp");
        const smtp = res.data.data;
        if (smtp) {
          setSmtpHost(smtp.smtpHost || "");
          setSmtpPort(smtp.smtpPort?.toString() || "587");
          setSmtpUser(smtp.smtpUser || "");
          setSmtpFromEmail(smtp.smtpFromEmail || "");
          setSmtpFromName(smtp.smtpFromName || "");
          setSmtpConfigured(smtp.isConfigured || false);
        }
      } catch {
        // SMTP not configured
      } finally {
        setSmtpChecked(true);
      }
    };
    checkSmtp();
  }, [user?.role]);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { limit: "50" };
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await api.get("/email-campaigns", { params });
      setCampaigns(res.data.data);
    } catch {
      showToast("Error al cargar campañas", "error");
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Fetch audience stats (re-fetch when lastVisitDays changes)
  useEffect(() => {
    const fetchAudienceStats = async () => {
      try {
        const days = form.audienceFilters?.lastVisitDays || 30;
        const res = await api.get<{ data: AudienceStatsResponse }>(
          `/email-campaigns/audience-stats?lastVisitDays=${days}`,
        );
        setAudienceStats(res.data.data);
      } catch {
        /* ignore */
      }
    };
    fetchAudienceStats();
  }, [form.audienceFilters?.lastVisitDays]);

  /**
   * Trae el resumen + la página actual de destinatarios de una campaña. Los
   * parámetros de paginación/búsqueda/filtro son opcionales (por defecto,
   * página 1 sin búsqueda ni filtro) para que las llamadas "de refresco" tras
   * enviar/pausar puedan seguir invocándose con solo el id, igual que antes.
   *
   * Se puede entrar en la pestaña de estadísticas SOLO vía `handleViewStats`
   * (desde la lista, que siempre parte de `stats === null` gracias a
   * `handleBack`), así que la carga inicial y los refetch posteriores
   * (paginación/búsqueda/filtro) se distinguen con `!stats` en `renderStats`
   * sin necesidad de un booleano de carga aparte: `recipientsTableLoading`
   * solo pinta un indicador discreto junto al título, nunca sustituye la
   * tabla ya renderizada.
   */
  const fetchStats = useCallback(
    async (
      id: string,
      params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: RecipientStatusFilter;
      },
    ) => {
      try {
        setRecipientsTableLoading(true);
        const query = new URLSearchParams();
        query.set("page", String(params?.page ?? 1));
        query.set(
          "limit",
          String(params?.limit ?? CAMPAIGN_STATS_DEFAULT_LIMIT),
        );
        if (params?.search) query.set("search", params.search);
        if (params?.status && params.status !== "all") {
          query.set("status", params.status);
        }
        const res = await api.get(
          `/email-campaigns/${id}/stats?${query.toString()}`,
        );
        setStats(res.data.data);
      } catch {
        showToast("Error al cargar estadísticas", "error");
      } finally {
        setRecipientsTableLoading(false);
      }
    },
    [showToast],
  );

  // Debounce del buscador de destinatarios (evita una petición por tecla) y
  // vuelta a la página 1 al cambiar el texto (mismo criterio que el filtro de
  // estado, ver el handler del <select>).
  useEffect(() => {
    const t = setTimeout(() => {
      setRecipientsSearch(recipientsSearchInput.trim());
      setRecipientsPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [recipientsSearchInput]);

  // Refetch de la tabla de destinatarios cuando cambia la página/tamaño/
  // búsqueda/filtro mientras se está viendo la pestaña de estadísticas de una
  // campaña concreta. No se dispara para otras pestañas ni si aún no hay
  // ninguna campaña seleccionada (p. ej. antes de `handleViewStats`).
  useEffect(() => {
    if (!statsCampaignId || view !== "stats") return;
    fetchStats(statsCampaignId, {
      page: recipientsPage,
      limit: recipientsLimit,
      search: recipientsSearch,
      status: recipientsStatusFilter,
    });
  }, [
    statsCampaignId,
    recipientsPage,
    recipientsLimit,
    recipientsSearch,
    recipientsStatusFilter,
    view,
    fetchStats,
  ]);

  const fetchAudiencePreview = async (campaignId: string) => {
    try {
      setLoadingPreview(true);
      const res = await api.post<{ data: AudiencePreview }>(
        `/email-campaigns/${campaignId}/preview-audience`,
      );
      setAudiencePreview(res.data.data);
    } catch {
      setAudiencePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const showMsg = (msg: string) => {
    showToast(msg, "success");
  };

  /** "YYYY-MM-DD" en hora LOCAL (no `toISOString`, que desplaza al cruzar medianoche en UTC). */
  const toDateInputValue = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  /** Rellena el rango de fechas de "Con reserva entre fechas" con el próximo lunes → domingo. */
  const applyNextWeekWindow = () => {
    const today = new Date();
    const daysUntilNextMonday = (8 - today.getDay()) % 7 || 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilNextMonday);
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);
    setForm({
      ...form,
      audienceFilters: {
        ...form.audienceFilters,
        reservationFrom: toDateInputValue(nextMonday),
        reservationTo: toDateInputValue(nextSunday),
      },
    });
    setAudiencePreview(null);
  };

  const handleCreate = () => {
    setEditingId(null);
    setView("templates");
  };

  const handleSelectTemplate = (templateId: string) => {
    const template = CAMPAIGN_TEMPLATES.find((t) => t.id === templateId);
    // Las plantillas solo aportan el FRAGMENTO de contenido: el envoltorio
    // (cabecera con el nombre del negocio, pie con {{unsubscribeUrl}}) es
    // siempre el mismo, lo ponga la plantilla o el generador de IA (ver
    // `handleGenerateAI` más abajo) — un único envoltorio visual, ver
    // `apps/dashboard/src/lib/email-layout.ts`.
    const content =
      templateId === "blank"
        ? getDefaultTemplate()
        : getCampaignTemplate(templateId);
    const htmlContent = wrapEmailContent({
      variant: "restaurant",
      brandName,
      content,
    });
    // `{{restaurantName}}` del asunto de la plantilla NO lo sustituye el
    // backend al enviar (a diferencia de `{{customerName}}`/`{{bookingUrl}}`,
    // que sí varían por destinatario, el nombre del negocio es constante para
    // toda la campaña): se sustituye aquí mismo, una sola vez, al elegir la
    // plantilla. Así el asunto por defecto ya sale personalizado en vez de
    // enviarse literal a los clientes.
    const subject = (template?.subject || "")
      .split("{{restaurantName}}")
      .join(brandName);
    setForm({
      name: "",
      subject,
      htmlContent,
      audienceType: "all",
      audienceFilters: {},
      scheduledAt: "",
      consentMode: "strict",
    });
    setAudiencePreview(null);
    setTestEmail("");
    setContentTab("visual");
    setView("editor");
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingId(campaign.id);
    setForm({
      name: campaign.name,
      subject: campaign.subject,
      htmlContent: campaign.htmlContent,
      audienceType: campaign.audienceType,
      audienceFilters: campaign.audienceFilters || {},
      scheduledAt: campaign.scheduledAt
        ? isoToLocalDatetimeInputValue(campaign.scheduledAt)
        : "",
      consentMode: campaign.consentMode === "soft" ? "soft" : "strict",
    });
    setAudiencePreview(null);
    setTestEmail("");
    setContentTab("visual");
    setView("editor");
  };

  /** Devuelve true si el guardado tuvo éxito (para poder encadenar acciones, ej. enviar). */
  const handleSave = async (): Promise<boolean> => {
    if (!form.name.trim() || !form.subject.trim() || !form.htmlContent.trim()) {
      showToast("Nombre, asunto y contenido son obligatorios", "error");
      return false;
    }
    // Defensa en profundidad: el botón "Guardar"/"Enviar Ahora" ya se
    // deshabilita con `reservationWindowError`, pero `handleSave` también se
    // invoca desde `handleSendFromEditor`/`handleSendTest` — repetir la
    // validación aquí evita guardar una campaña que el backend resolvería con
    // audiencia vacía (falta alguna fecha o "desde" > "hasta").
    if (form.audienceType === "reservation_window") {
      const { reservationFrom, reservationTo } = form.audienceFilters;
      if (!reservationFrom || !reservationTo || reservationFrom > reservationTo) {
        showToast(
          "Selecciona un rango de fechas válido (desde ≤ hasta) para la audiencia por reserva",
          "error",
        );
        return false;
      }
    }

    try {
      setSaving(true);

      // Build audience filters, injecting customEmails if needed
      const audienceFilters = { ...form.audienceFilters };
      if (form.audienceType === "custom" && customEmailsText.trim()) {
        audienceFilters.customEmails = customEmailsText
          .split(/[\n,;]+/)
          .map((e) => e.trim())
          .filter((e) => e && e.includes("@"));
      }

      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        htmlContent: form.htmlContent,
        audienceType: form.audienceType,
        audienceFilters:
          Object.keys(audienceFilters).length > 0 ? audienceFilters : undefined,
        scheduledAt: form.scheduledAt
          ? new Date(form.scheduledAt).toISOString()
          : null,
        consentMode: form.consentMode,
      };

      let saved: { id: string; htmlContent?: string };
      if (editingId) {
        const res = await api.put(`/email-campaigns/${editingId}`, payload);
        saved = res.data.data;
        showMsg("Campaña actualizada");
      } else {
        const res = await api.post("/email-campaigns", payload);
        saved = res.data.data;
        setEditingId(saved.id);
        showMsg("Campaña creada");
      }

      // El backend devuelve el HTML ya saneado (imágenes en base64
      // sustituidas por su URL pública subida a Storage). Si no se
      // sincroniza aquí, el siguiente guardado volvería a subir la misma
      // imagen de nuevo (el form seguiría con el base64 original).
      if (saved?.htmlContent && saved.htmlContent !== form.htmlContent) {
        const sanitizedHtml = saved.htmlContent;
        setForm((f) => ({ ...f, htmlContent: sanitizedHtml }));
      }

      await fetchCampaigns();
      return true;
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al guardar", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: "¿Eliminar esta campaña?", message: "Esta acción no se puede deshacer.", confirmText: "Sí, eliminar", danger: true }))) return;
    try {
      await api.delete(`/email-campaigns/${id}`);
      showMsg("Campaña eliminada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al eliminar", "error");
    }
  };

  const handleSend = async (id: string) => {
    // Evita clics repetidos mientras se calcula la audiencia o se envía
    // (la llamada de red previa al confirm no tenía ningún indicador).
    if (sendingId) return;
    setSendingId(id);
    try {
      let confirmMessage =
        "Se enviará ahora a todos los destinatarios elegibles. Esta acción no se puede deshacer.";
      try {
        const previewRes = await api.post<{ data: AudiencePreview }>(
          `/email-campaigns/${id}/preview-audience`,
        );
        const preview = previewRes.data.data;
        if (preview.consentMode === "soft") {
          const withoutConsent = Math.max(
            preview.eligibleSoft - preview.withConsent,
            0,
          );
          confirmMessage = `Se enviará a ${preview.eligibleSoft} destinatarios, ${withoutConsent} de ellos sin consentimiento explícito de marketing (modo permisivo, LSSI art. 21.2). Todos los emails incluyen enlace de baja. Esta acción no se puede deshacer.`;
        } else {
          confirmMessage = `Se enviará ahora a ${preview.eligible} destinatarios elegibles (con consentimiento explícito de marketing). Esta acción no se puede deshacer.`;
        }
      } catch {
        // Si falla el cálculo de audiencia, se usa el mensaje genérico de arriba
      }
      if (
        !(await confirm({
          title: "¿Enviar esta campaña?",
          message: confirmMessage,
          confirmText: "Sí, enviar",
        }))
      )
        return;
      try {
        const res = await api.post(`/email-campaigns/${id}/send`);
        showMsg(res.data.message);
        fetchCampaigns();
        if (view === "stats") {
          fetchStats(id, {
            page: recipientsPage,
            limit: recipientsLimit,
            search: recipientsSearch,
            status: recipientsStatusFilter,
          });
        }
      } catch (err: any) {
        showToast(err.response?.data?.message || "Error al enviar", "error");
      }
    } finally {
      setSendingId(null);
    }
  };

  /**
   * "Enviar Ahora" desde el editor: guarda los cambios pendientes del
   * formulario ANTES de calcular/enviar la audiencia, para que lo que
   * el backend usa para decidir destinatarios (consentMode/audienceType/
   * audienceFilters/contenido) coincida siempre con lo que el panel muestra.
   */
  const handleSendFromEditor = async () => {
    if (!editingId) return;
    const saved = await handleSave();
    if (!saved) return;
    await handleSend(editingId);
  };

  /**
   * Envía un email de PRUEBA de la campaña a una dirección concreta. Llega
   * EXACTAMENTE igual que al cliente final: mismo asunto (sin prefijos),
   * mismas sustituciones ({{customerName}}, {{bookingUrl}}...), mismo
   * tracking y mismo pie de baja — con el SMTP de la organización. No crea
   * destinatarios ni cuenta en las estadísticas de la campaña.
   *
   * El endpoint lee el asunto y el HTML de la campaña YA GUARDADA (el body
   * solo lleva el email): el HTML persistido ya pasó por el mismo saneado de
   * imágenes que usa el envío real, así que aceptar el HTML del formulario
   * sin guardar antes haría que la prueba mintiera. Por eso se guarda
   * primero, igual que "Enviar Ahora" (`handleSendFromEditor`).
   */
  const handleSendTest = async () => {
    if (sendingTest) return;
    const email = testEmail.trim();
    if (!email || !email.includes("@")) {
      showToast("Introduce un email válido para la prueba", "error");
      return;
    }
    try {
      setSendingTest(true);
      const saved = await handleSave();
      if (!saved || !editingId) return;
      const res = await api.post(`/email-campaigns/${editingId}/send-test`, {
        email,
      });
      showMsg(res.data.message);
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, "Error al enviar la prueba"), "error");
    } finally {
      setSendingTest(false);
    }
  };

  const handlePause = async (id: string) => {
    try {
      await api.post(`/email-campaigns/${id}/pause`);
      showMsg("Campaña pausada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al pausar", "error");
    }
  };

  const handleResume = async (id: string) => {
    try {
      await api.post(`/email-campaigns/${id}/resume`);
      showMsg("Campaña reanudada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al reanudar", "error");
    }
  };

  const handleCancel = async (id: string) => {
    if (!(await confirm({ title: "¿Cancelar esta campaña?", message: "La campaña se detendrá y no continuará el envío.", confirmText: "Sí, cancelar", danger: true }))) return;
    try {
      await api.post(`/email-campaigns/${id}/cancel`);
      showMsg("Campaña cancelada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al cancelar", "error");
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await api.post(`/email-campaigns/${id}/duplicate`);
      showMsg("Campaña duplicada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al duplicar", "error");
    }
  };

  const handleViewStats = (campaign: Campaign) => {
    // Reinicia paginación/búsqueda/filtro de una vista de estadísticas anterior
    // (si la hubiera): el efecto de refetch se dispara solo por el cambio de
    // `statsCampaignId`, así que no hace falta llamar a `fetchStats` aquí.
    setRecipientsPage(1);
    setRecipientsSearchInput("");
    setRecipientsSearch("");
    setRecipientsStatusFilter("all");
    setStatsCampaignId(campaign.id);
    setView("stats");
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      showToast("Escribe qué quieres comunicar", "error");
      return;
    }
    try {
      setGeneratingAI(true);
      const res = await api.post("/email-campaigns/generate-ai", {
        prompt: aiPrompt.trim(),
      });
      const { subject, body, html } = res.data.data;
      // El backend ya solo devuelve `{ subject, body }` (el fragmento de
      // contenido; el envoltorio lo pone siempre este módulo, igual que a
      // las plantillas). Rama de compatibilidad temporal: backend (Railway)
      // y dashboard (Vercel) se despliegan por separado, así que puede haber
      // una ventana en la que el backend todavía sea el anterior y devuelva
      // `html` ya envuelto — se usa tal cual en ese caso. Si ninguno de los
      // dos llega como string (ventana de despliegue asimétrica en sentido
      // contrario: dashboard ya actualizado, backend aún sin desplegar y sin
      // devolver ninguno de los dos campos), se conserva el contenido previo
      // del formulario en vez de vaciarlo — evita el TypeError de
      // `handleSave` (`form.htmlContent.trim()` sobre `undefined`) y la
      // pérdida silenciosa del borrador. Borrar esta rama de compatibilidad
      // cuando ambos estén desplegados.
      const htmlContent =
        typeof body === "string"
          ? wrapEmailContent({ variant: "restaurant", brandName, content: body })
          : typeof html === "string"
            ? html
            : form.htmlContent;
      setForm({
        ...form,
        subject: subject || form.subject,
        htmlContent,
      });
      setShowAIGenerator(false);
      setAiPrompt("");
      showToast("Email generado con IA correctamente", "success");
    } catch (err: any) {
      showToast(
        err.response?.data?.message || "Error al generar con IA",
        "error",
      );
    } finally {
      setGeneratingAI(false);
    }
  };

  /**
   * Copia un token `{{...}}` al portapapeles desde la guía de variables.
   * Complementa (no sustituye) al "insertar variable" propio de los bloques
   * de Texto y a las sugerencias de los campos de enlace de
   * `EmailBlockEditor`: los bloques Título y HTML libre no tienen un punto
   * de inserción dedicado, así que "copiar y pegar" sigue siendo el único
   * camino para ellos (y para la pestaña HTML en crudo).
   */
  const handleCopyVariable = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      showToast("Copiado — pégalo donde quieras", "success");
    } catch {
      showToast("No se pudo copiar. Selecciona el texto manualmente", "error");
    }
  };

  const handleBack = () => {
    setView("list");
    setEditingId(null);
    setStats(null);
    setStatsCampaignId(null);
    setRecipientsPage(1);
    setRecipientsSearchInput("");
    setRecipientsSearch("");
    setRecipientsStatusFilter("all");
    setAudiencePreview(null);
  };

  // ============================================================================
  // RENDER: TEMPLATE SELECTOR
  // ============================================================================

  const renderTemplateSelector = () => (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="mb-4 md:mb-6 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Elige una plantilla
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Selecciona una plantilla para comenzar tu campaña
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {CAMPAIGN_TEMPLATES.map((tpl) => {
          const Icon = tpl.icon;
          return (
            <button
              key={tpl.id}
              onClick={() => handleSelectTemplate(tpl.id)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-left hover:shadow-md hover:border-primary-300 transition-all group"
            >
              <div
                className={`w-12 h-12 rounded-xl ${tpl.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{tpl.name}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                {tpl.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: LIST VIEW
  // ============================================================================

  const renderList = () => (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary-600" />
            Campañas de Email
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Envía newsletters, promociones y comunicaciones masivas a tus
            clientes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role !== "owner" && (
            <button
              onClick={() => setView("smtp")}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              <Server className="w-4 h-4" />
              <span className="hidden sm:inline">SMTP</span>
              {smtpConfigured && (
                <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              )}
            </button>
          )}
          {canManageCampaigns && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Nueva Campaña
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { value: "all", label: "Todas" },
          { value: "draft", label: "Borradores" },
          { value: "scheduled", label: "Programadas" },
          { value: "sending", label: "Enviando" },
          { value: "sent", label: "Enviadas" },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilterStatus(f.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === f.value
                ? "bg-primary-100 text-primary-700"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            Cargando campañas...
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No hay campañas</p>
            <p className="text-gray-400 text-sm mt-1">
              Crea tu primera campaña para empezar
            </p>
            {canManageCampaigns && (
              <button
                onClick={handleCreate}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
              >
                Crear Campaña
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {campaigns.map((c) => {
              const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
              const StatusIcon = sc.icon;
              return (
                <div
                  key={c.id}
                  className="p-4 md:px-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {c.name}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        <Mail className="w-3.5 h-3.5 inline mr-1" />
                        {c.subject}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(c.createdAt).toLocaleDateString("es-ES")}
                        </span>
                        {c.scheduledAt && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Clock className="w-3 h-3" />
                            Programada:{" "}
                            {new Date(c.scheduledAt).toLocaleString("es-ES", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })}
                          </span>
                        )}
                        {c.totalRecipients > 0 && (
                          <>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {c.totalRecipients} destinatarios
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {c.openCount} abiertos
                            </span>
                            <span className="flex items-center gap-1">
                              <MousePointerClick className="w-3 h-3" />
                              {c.clickCount} clics
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {canManageCampaigns &&
                        ["draft", "scheduled"].includes(c.status) && (
                          <>
                            <button
                              onClick={() => handleEdit(c)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4 text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleSend(c.id)}
                              disabled={sendingId === c.id}
                              className="p-2 hover:bg-green-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              title={
                                sendingId === c.id
                                  ? "Calculando audiencia..."
                                  : "Enviar ahora"
                              }
                            >
                              {sendingId === c.id ? (
                                <Loader2 className="w-4 h-4 text-green-600 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 text-green-600" />
                              )}
                            </button>
                          </>
                        )}
                      {canManageCampaigns && c.status === "sending" && (
                        <button
                          onClick={() => handlePause(c.id)}
                          className="p-2 hover:bg-orange-50 rounded-lg"
                          title="Pausar"
                        >
                          <Pause className="w-4 h-4 text-orange-600" />
                        </button>
                      )}
                      {canManageCampaigns && c.status === "paused" && (
                        <button
                          onClick={() => handleResume(c.id)}
                          className="p-2 hover:bg-green-50 rounded-lg"
                          title="Reanudar"
                        >
                          <Play className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                      {canManageCampaigns &&
                        !["sent", "cancelled"].includes(c.status) &&
                        c.status !== "draft" && (
                          <button
                            onClick={() => handleCancel(c.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                            title="Cancelar"
                          >
                            <XCircle className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      {["sent", "sending", "paused"].includes(c.status) && (
                        <button
                          onClick={() => handleViewStats(c)}
                          className="p-2 hover:bg-blue-50 rounded-lg"
                          title="Estadísticas"
                        >
                          <BarChart3 className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                      {canManageCampaigns && (
                        <button
                          onClick={() => handleDuplicate(c.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Duplicar"
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                      {canManageCampaigns &&
                        ["draft", "scheduled", "cancelled"].includes(
                          c.status,
                        ) && (
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: EDITOR VIEW
  // ============================================================================

  const renderEditor = () => {
    const allStats = getSegmentStats(audienceStats, "all");
    const recentStats = getSegmentStats(audienceStats, "recent");
    // Solo "all"/"recent" tienen un desglose agregado real; para "custom" (y
    // cualquier otro audienceType) no hay stats aplicables: no fabricar
    // cifras de toda la organización para una audiencia distinta.
    const activeStats =
      form.audienceType === "recent"
        ? recentStats
        : form.audienceType === "all"
          ? allStats
          : null;
    const eligibleFor = (s: AudienceSegmentStats | null): number | null =>
      s
        ? s.degraded
          ? s.eligibleStrict
          : form.consentMode === "soft"
            ? s.eligibleSoft
            : s.eligibleStrict
        : null;
    const activeEligible = eligibleFor(activeStats);
    // Validación de la ventana de fechas de "Con reserva entre fechas": el
    // backend devuelve audiencia vacía si falta alguna fecha o from > to, así
    // que aquí se bloquea Guardar/Enviar ANTES de llegar a esa situación.
    // Comparación como string "YYYY-MM-DD": el orden lexicográfico coincide
    // con el cronológico, no hace falta parsear a Date.
    const reservationWindowError =
      form.audienceType === "reservation_window"
        ? !form.audienceFilters.reservationFrom ||
          !form.audienceFilters.reservationTo
          ? "Selecciona ambas fechas (desde y hasta)."
          : form.audienceFilters.reservationFrom >
              form.audienceFilters.reservationTo
            ? "La fecha \"Desde\" no puede ser posterior a \"Hasta\"."
            : null
        : null;

    return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {editingId ? "Editar Campaña" : "Nueva Campaña"}
            </h1>
            <p className="text-gray-500 text-sm">
              Configura el contenido, audiencia y programación
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editingId && (
            <button
              onClick={handleSendFromEditor}
              disabled={
                saving || sendingId === editingId || !!reservationWindowError
              }
              title={reservationWindowError || undefined}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
            >
              {sendingId === editingId ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {saving
                  ? "Guardando..."
                  : sendingId === editingId
                    ? "Calculando..."
                    : "Enviar Ahora"}
              </span>
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !!reservationWindowError}
            title={reservationWindowError || undefined}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Main content - 2/3 */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              Información básica
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la campaña
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Newsletter Abril 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto del email
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) =>
                    setForm({ ...form, subject: e.target.value })
                  }
                  placeholder="Ej: ¡Novedades de tu restaurante favorito!"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  En el asunto puedes usar {"{{customerName}}"} y{" "}
                  {"{{email}}"} (se sustituyen al enviar). El resto de
                  variables solo funcionan en el contenido del email.
                </p>
              </div>
            </div>
          </div>

          {/* AI Generator */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 overflow-hidden">
            <button
              onClick={() => setShowAIGenerator(!showAIGenerator)}
              className="w-full flex items-center justify-between px-4 md:px-6 py-3 hover:bg-purple-50/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-semibold text-purple-900">
                  Generar con IA
                </span>
                <span className="text-xs text-purple-500 hidden sm:inline">
                  — Describe lo que quieres y la IA creará el email
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-purple-400 transition-transform ${showAIGenerator ? "rotate-180" : ""}`}
              />
            </button>
            {showAIGenerator && (
              <div className="px-4 md:px-6 pb-4 space-y-3">
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ej: Quiero enviar a mis clientes un email promocionando nuestro nuevo menú degustación de primavera con mariscos frescos a 55€ por persona..."
                  rows={3}
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white placeholder:text-purple-300"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-purple-400">
                    {aiPrompt.length}/1000 caracteres
                  </p>
                  <button
                    onClick={handleGenerateAI}
                    disabled={generatingAI || !aiPrompt.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingAI ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Generar email
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Variables disponibles */}
          <div className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
            <button
              onClick={() => setShowVariablesGuide(!showVariablesGuide)}
              className="w-full flex items-center justify-between px-4 md:px-6 py-3 hover:bg-blue-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">
                  Variables disponibles
                </span>
                <span className="text-xs text-blue-500 hidden sm:inline">
                  — Marcadores que se sustituyen al enviar
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-blue-400 transition-transform ${showVariablesGuide ? "rotate-180" : ""}`}
              />
            </button>
            {showVariablesGuide && (
              <div className="px-4 md:px-6 pb-4 space-y-3">
                <p className="text-xs text-blue-700">
                  Úsalas en el <strong>contenido del email</strong>;{" "}
                  {"{{customerName}}"} y {"{{email}}"} funcionan también en
                  el asunto. Al enviar, cada marcador se sustituye
                  automáticamente por su valor real para cada destinatario.
                </p>
                <div className="bg-white rounded-lg border border-blue-100 divide-y divide-gray-100 overflow-hidden">
                  {CAMPAIGN_VARIABLES.map((v) => (
                    <div
                      key={v.token}
                      className={`flex items-start justify-between gap-3 px-3 py-2.5 ${
                        v.highlight ? "bg-amber-50" : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <code
                          className={`inline-block text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${
                            v.highlight
                              ? "bg-amber-100 text-amber-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {v.token}
                        </code>
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                          {v.description}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCopyVariable(v.token)}
                        title="Copiar"
                        className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md text-xs font-medium transition-colors"
                      >
                        <ClipboardCopy className="w-3 h-3" />
                        <span className="hidden sm:inline">Copiar</span>
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <strong>{"{{reservationUrl}}"}</strong> es ideal como enlace
                  del botón en campañas a reservas de una franja: cada
                  cliente abre SU reserva y puede elegir o cambiar su menú.
                </p>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                Contenido del email
              </h2>
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setContentTab("visual")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    contentTab === "visual"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Editor Visual
                </button>
                <button
                  onClick={() => setContentTab("html")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    contentTab === "html"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  HTML
                </button>
              </div>
            </div>
            <div className="p-4 md:p-6">
              {contentTab === "visual" ? (
                <EmailBlockEditor
                  value={form.htmlContent}
                  onChange={(html) => setForm({ ...form, htmlContent: html })}
                  brandName={brandName}
                  variant="restaurant"
                  variables={CAMPAIGN_VARIABLES}
                />
              ) : (
                <textarea
                  value={form.htmlContent}
                  onChange={(e) =>
                    setForm({ ...form, htmlContent: e.target.value })
                  }
                  rows={18}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
                  placeholder="<html>...</html>"
                />
              )}
              {/* La sustitución de `{{bookingUrl}}` solo ocurre en el CONTENIDO
                  del email, nunca en el asunto (ver `runCampaignSending`), y el
                  local de destino lo fija la organización, no el selector: por
                  eso el aviso vive aquí y dice a qué local apunta. */}
              <p className="text-xs text-gray-400 mt-2">
                {bookingLocation ? (
                  <>
                    En el contenido puedes usar {"{{bookingUrl}}"}: al enviar se
                    sustituye por el enlace a tu página de reservas (
                    {bookingLocation.name}). En el asunto no se sustituye.
                  </>
                ) : (
                  <>
                    No hay ningún local activo con página de reservas, así que
                    el botón de reserva ({"{{bookingUrl}}"}) se enviará sin
                    enlace.
                  </>
                )}
              </p>
              <EmailHtmlSizeNotice html={form.htmlContent} />
            </div>
          </div>
        </div>

        {/* Sidebar - 1/3 */}
        <div className="space-y-4">
          {/* Audience */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-600" />
              Audiencia
            </h2>

            <div className="space-y-3">
              {/* Segment selector as cards */}
              <div className="space-y-2">
                {[
                  {
                    value: "all",
                    label: "Todos los clientes",
                    icon: "👥",
                    count: allStats?.total,
                    eligible: eligibleFor(allStats),
                  },
                  {
                    value: "recent",
                    label: "Con reserva reciente",
                    icon: "📅",
                    count: recentStats?.total,
                    eligible: eligibleFor(recentStats),
                    sub: `Últimos ${form.audienceFilters?.lastVisitDays || 30} días`,
                  },
                  {
                    value: "reservation_window",
                    label: "Con reserva entre fechas",
                    icon: "🍽️",
                    count: null,
                    eligible: null,
                    sub: "Ideal para el menú de la semana",
                  },
                  {
                    value: "custom",
                    label: "Personalizado",
                    icon: "✉️",
                    count: null,
                    eligible: null,
                  },
                ].map((seg) => (
                  <button
                    key={seg.value}
                    onClick={() => {
                      setForm({
                        ...form,
                        audienceType: seg.value,
                        audienceFilters:
                          seg.value === "recent" ? { lastVisitDays: 30 } : {},
                      });
                      setCustomEmailsText("");
                      // El preview estimado pertenece al segmento/filtros con los
                      // que se calculó: al cambiar de audiencia deja de ser
                      // válido, se limpia para no mostrar una cifra engañosa.
                      setAudiencePreview(null);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
                      form.audienceType === seg.value
                        ? "bg-primary-50 border-2 border-primary-500 text-primary-800 font-medium"
                        : "bg-gray-50 border-2 border-transparent hover:border-gray-200 text-gray-700"
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">{seg.icon}</span>
                    <div className="flex-1 min-w-0">
                      <span className="block">{seg.label}</span>
                      {seg.sub && (
                        <span className="block text-xs text-gray-400 font-normal">
                          {seg.sub}
                        </span>
                      )}
                    </div>
                    {seg.count != null && (
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            form.audienceType === seg.value
                              ? "bg-primary-100 text-primary-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {seg.count}
                        </span>
                        {seg.eligible != null && (
                          <span className="text-[10px] text-green-600 font-medium">
                            {seg.eligible} elegibles
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Recent: customizable days */}
              {form.audienceType === "recent" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clientes con reserva en los últimos...
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={form.audienceFilters.lastVisitDays || 30}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          audienceFilters: {
                            ...form.audienceFilters,
                            lastVisitDays: parseInt(e.target.value) || 30,
                          },
                        })
                      }
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center"
                    />
                    <span className="text-sm text-gray-600">días</span>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    {[7, 15, 30, 60, 90].map((d) => (
                      <button
                        key={d}
                        onClick={() =>
                          setForm({
                            ...form,
                            audienceFilters: {
                              ...form.audienceFilters,
                              lastVisitDays: d,
                            },
                          })
                        }
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          form.audienceFilters.lastVisitDays === d
                            ? "bg-primary-100 text-primary-700"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reservation window: date range */}
              {form.audienceType === "reservation_window" && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">
                    Clientes con una reserva activa en el periodo elegido —
                    ideal para enviar el menú de la semana.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Desde
                      </label>
                      <input
                        type="date"
                        value={form.audienceFilters.reservationFrom || ""}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            audienceFilters: {
                              ...form.audienceFilters,
                              reservationFrom: e.target.value,
                            },
                          });
                          setAudiencePreview(null);
                        }}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hasta
                      </label>
                      <input
                        type="date"
                        value={form.audienceFilters.reservationTo || ""}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            audienceFilters: {
                              ...form.audienceFilters,
                              reservationTo: e.target.value,
                            },
                          });
                          setAudiencePreview(null);
                        }}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={applyNextWeekWindow}
                    className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    📆 Semana que viene (lun–dom)
                  </button>
                  {reservationWindowError && (
                    <p className="mt-2 flex items-start gap-1 text-xs text-red-600">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      {reservationWindowError}
                    </p>
                  )}
                </div>
              )}

              {/* Custom: email list */}
              {form.audienceType === "custom" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emails destinatarios
                  </label>
                  <textarea
                    value={customEmailsText}
                    onChange={(e) => setCustomEmailsText(e.target.value)}
                    rows={5}
                    placeholder={
                      "cliente1@email.com\ncliente2@email.com\ncliente3@email.com"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Un email por línea. También puedes separarlos con comas.
                    {customEmailsText && (
                      <span className="block mt-0.5 text-primary-600 font-medium">
                        {
                          customEmailsText
                            .split(/[\n,;]+/)
                            .filter((e) => e.trim() && e.trim().includes("@"))
                            .length
                        }{" "}
                        emails válidos
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Info box: desglose completo del segmento activo */}
              {activeStats && form.audienceType !== "custom" && (
                <div className="bg-blue-50 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-2 font-semibold text-blue-800 mb-2">
                    <Users className="w-4 h-4" />
                    Info de audiencia
                  </div>
                  <div className="space-y-1 text-blue-700 text-xs">
                    <div className="flex items-center justify-between">
                      <span>Total clientes</span>
                      <span className="font-semibold">{activeStats.total}</span>
                    </div>
                    {activeStats.degraded ? (
                      <p className="text-blue-500 italic">
                        Desglose no disponible
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span>Sin email</span>
                          <span className="font-semibold">
                            {activeStats.withoutEmail}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>De baja</span>
                          <span className="font-semibold">
                            {activeStats.unsubscribed}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Rebotados</span>
                          <span className="font-semibold">
                            {activeStats.bounced}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Con consentimiento explícito</span>
                          <span className="font-semibold">
                            {activeStats.withConsent}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-blue-200">
                    <span className="font-semibold text-blue-900">
                      → Se enviará a
                    </span>
                    <span className="text-base font-bold text-primary-700">
                      {activeEligible}
                    </span>
                  </div>
                </div>
              )}

              {/* Toggle: modo de consentimiento (soft opt-in LSSI art. 21.2) */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Enviar también a clientes sin consentimiento explícito
                    </p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      Modo permitido para clientes con los que ya tienes
                      relación (LSSI art. 21.2). Todos los emails incluyen
                      enlace de baja y las bajas se respetan siempre.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        consentMode: form.consentMode === "soft" ? "strict" : "soft",
                      })
                    }
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                      form.consentMode === "soft"
                        ? "bg-primary-600"
                        : "bg-gray-300"
                    }`}
                    aria-pressed={form.consentMode === "soft"}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        form.consentMode === "soft"
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                {form.consentMode === "soft" && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {activeStats && !activeStats.degraded ? (
                      <span>
                        Pasarás de <strong>{activeStats.eligibleStrict}</strong>{" "}
                        a <strong>{activeStats.eligibleSoft}</strong>{" "}
                        destinatarios.
                      </span>
                    ) : (
                      <span>
                        Se incluirá también a los clientes de tu lista sin
                        consentimiento explícito de marketing. Las bajas
                        se siguen respetando siempre.
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Preview button */}
              {editingId && (
                <button
                  onClick={() => fetchAudiencePreview(editingId)}
                  disabled={loadingPreview}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                >
                  <Search className="w-4 h-4" />
                  {loadingPreview ? "Calculando..." : "Ver audiencia estimada"}
                </button>
              )}

              {audiencePreview && (
                <div className="bg-green-50 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-2 font-semibold text-green-800 mb-2">
                    <Users className="w-4 h-4" />
                    Audiencia estimada (
                    {audiencePreview.consentMode === "soft"
                      ? "modo permisivo"
                      : "modo estricto"}
                    )
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-green-700 text-xs">
                    <p>
                      Total: <strong>{audiencePreview.total}</strong>
                    </p>
                    <p>
                      Sin email: <strong>{audiencePreview.withoutEmail}</strong>
                    </p>
                    <p>
                      De baja: <strong>{audiencePreview.unsubscribed}</strong>
                    </p>
                    <p>
                      Rebotados: <strong>{audiencePreview.bounced}</strong>
                    </p>
                  </div>
                  <p className="mt-2 pt-2 border-t border-green-200 font-semibold text-green-800">
                    Se enviará a: <strong>{audiencePreview.eligible}</strong>{" "}
                    destinatarios
                  </p>
                  {audiencePreview.preview.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {audiencePreview.preview.slice(0, 5).map((c) => (
                        <p
                          key={c.id}
                          className={`text-xs truncate ${
                            c.eligible
                              ? "text-green-600"
                              : "text-gray-400 line-through"
                          }`}
                        >
                          {c.name} — {c.email}
                        </p>
                      ))}
                      {audiencePreview.total > 5 && (
                        <p className="text-xs text-green-500">
                          y {audiencePreview.total - 5} más...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-600" />
              Programación
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha y hora de envío
                </label>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) =>
                    setForm({ ...form, scheduledAt: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Deja vacío para enviar manualmente
                </p>
              </div>

              {form.scheduledAt && (
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Se enviará el{" "}
                    {new Date(form.scheduledAt).toLocaleString("es-ES", {
                      dateStyle: "long",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Help */}
          <div className="bg-primary-50 rounded-xl border border-primary-100 p-4">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">
              Consejos
            </h3>
            <ul className="text-xs text-primary-700 space-y-1.5">
              <li>
                {form.consentMode === "soft" ? (
                  <>
                    • Modo permisivo: se envía a todos los clientes con{" "}
                    <strong>email</strong> salvo los que se han dado de{" "}
                    <strong>baja</strong> o han rebotado
                  </>
                ) : (
                  <>
                    • Solo se envía a clientes con <strong>email</strong> y{" "}
                    <strong>consentimiento de marketing</strong>
                  </>
                )}
              </li>
              <li>
                • Usa {"{{customerName}}"} para personalizar (funciona en el
                contenido y en el asunto; el resto de variables, solo en el
                contenido)
              </li>
              <li>
                • Los envíos se realizan con rate-limiting para no saturar SMTP
              </li>
              <li>• Puedes pausar y reanudar campañas en curso</li>
              <li>• El tracking de aperturas y clics es automático</li>
            </ul>
          </div>

          {/* Send Test */}
          {editingId && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-orange-500" />
                Enviar prueba
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Se guardan los cambios y se envía un email de prueba con el
                SMTP de tu organización. Llega{" "}
                <strong>exactamente igual</strong> que le llegaría a un
                cliente: mismo asunto, mismas sustituciones, mismo tracking y
                mismo pie de baja. No cuenta como envío real ni afecta a las
                estadísticas de la campaña.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email de prueba
                  </label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  onClick={handleSendTest}
                  disabled={sendingTest || saving}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  {sendingTest ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FlaskConical className="w-4 h-4" />
                  )}
                  {sendingTest ? "Enviando..." : "Enviar email de prueba"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  // ============================================================================
  // RENDER: STATS VIEW
  // ============================================================================

  const renderStats = () => {
    if (!stats) {
      return (
        <div className="min-h-screen bg-gray-50 p-3 md:p-6">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              Cargando estadísticas...
            </h1>
          </div>
        </div>
      );
    }

    const {
      campaign,
      byStatus,
      openRate,
      clickRate,
      unsubscribedCount,
      reservedCount,
    } =
      stats;

    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        {/* Header */}
        <div className="mb-4 md:mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {campaign.name}
              </h1>
              <p className="text-gray-500 text-sm">{campaign.subject}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManageCampaigns && campaign.status === "sending" && (
              <button
                onClick={() => handlePause(campaign.id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-200"
              >
                <Pause className="w-4 h-4" />
                Pausar
              </button>
            )}
            {canManageCampaigns && campaign.status === "paused" && (
              <button
                onClick={() => handleResume(campaign.id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200"
              >
                <Play className="w-4 h-4" />
                Reanudar
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Users className="w-4 h-4" />
              Destinatarios
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {campaign.totalRecipients}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Send className="w-4 h-4" />
              Enviados
            </div>
            <p className="text-2xl font-bold text-green-600">
              {campaign.sentCount}
            </p>
            {campaign.failedCount > 0 && (
              <p className="text-xs text-red-500 mt-0.5">
                {campaign.failedCount} fallidos
              </p>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Eye className="w-4 h-4" />
              Tasa de apertura
            </div>
            <p className="text-2xl font-bold text-blue-600">{openRate}%</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {(byStatus.opened || 0) + (byStatus.clicked || 0)} abiertos
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <MousePointerClick className="w-4 h-4" />
              Tasa de clics
            </div>
            <p className="text-2xl font-bold text-purple-600">{clickRate}%</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {byStatus.clicked || 0} clics
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <UserX className="w-4 h-4" />
              Bajas
            </div>
            <p className="text-2xl font-bold text-red-600">
              {unsubscribedCount}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4" />
              Reservas
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              {reservedCount ?? 0}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">desde el email</p>
          </div>
        </div>

        {/* Progress Bar */}
        {campaign.status === "sending" && campaign.totalRecipients > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progreso de envío
              </span>
              <span className="text-sm text-gray-500">
                {campaign.sentCount + campaign.failedCount} /{" "}
                {campaign.totalRecipients}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all"
                style={{
                  width: `${((campaign.sentCount + campaign.failedCount) / campaign.totalRecipients) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Recipients Table */}
        <CampaignRecipientsTable
          recipients={stats.recipients}
          total={stats.total}
          page={stats.page}
          limit={stats.limit}
          onPageChange={setRecipientsPage}
          onLimitChange={(l) => {
            setRecipientsLimit(l);
            setRecipientsPage(1);
          }}
          searchValue={recipientsSearchInput}
          onSearchValueChange={setRecipientsSearchInput}
          status={recipientsStatusFilter}
          onStatusChange={(s) => {
            setRecipientsStatusFilter(s);
            setRecipientsPage(1);
          }}
          loading={recipientsTableLoading}
        />
      </div>
    );
  };

  // ============================================================================
  // SMTP HANDLERS
  // ============================================================================

  const handleSaveSmtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSmtpLoading(true);
    setSmtpMessage(null);

    try {
      const res = await api.put("/organizations/me/smtp", {
        smtpHost,
        smtpPort: smtpPort ? parseInt(smtpPort) : 587,
        smtpUser,
        smtpPassword: smtpPassword || undefined,
        smtpFromEmail,
        smtpFromName,
      });
      if (res.data.success) {
        setSmtpMessage({
          type: "success",
          text: "Configuración SMTP guardada correctamente",
        });
        setSmtpConfigured(true);
        setSmtpPassword("");
      }
    } catch (err: unknown) {
      setSmtpMessage({
        type: "error",
        text: getApiErrorMessage(err, "Error al guardar configuración SMTP"),
      });
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    setSmtpMessage(null);

    try {
      const res = await api.post("/organizations/me/smtp/test");
      if (res.data.success) {
        setSmtpMessage({
          type: "success",
          // El backend nombra el destinatario REAL (el email de la cuenta con
          // la que se ha iniciado sesión, no el remitente), que es la causa
          // habitual de "he enviado el test y no me llega".
          text:
            res.data.message ||
            "Email de prueba enviado. Revisa tu bandeja de entrada.",
        });
      }
    } catch (err: unknown) {
      setSmtpMessage({
        type: "error",
        text: getApiErrorMessage(err, "Error al enviar email de prueba"),
      });
    } finally {
      setSmtpTesting(false);
    }
  };

  // ============================================================================
  // SMTP CONFIG VIEW
  // ============================================================================

  const renderSmtpConfig = () => (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => setView("list")}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Configuración SMTP
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Configura tu servidor SMTP para enviar campañas de email
            </p>
          </div>
        </div>

        {/* SMTP Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Server className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">
                Servidor SMTP
              </h3>
              <p className="text-sm text-gray-500">
                Puedes usar servicios como Gmail, Brevo, SendGrid, Mailgun, etc.
              </p>
            </div>
            {smtpConfigured && (
              <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 px-2 py-1 rounded-full">
                <CheckCircle className="w-4 h-4" />
                Configurado
              </span>
            )}
          </div>

          <form onSubmit={handleSaveSmtp} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Host SMTP
                </label>
                <input
                  type="text"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.ejemplo.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Puerto
                </label>
                <input
                  type="text"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  placeholder="587"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usuario SMTP
                </label>
                <input
                  type="text"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  placeholder="usuario@ejemplo.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña SMTP
                </label>
                <input
                  type="password"
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder={smtpConfigured ? "••••••••" : "Contraseña"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                {smtpConfigured && (
                  <p className="text-xs text-gray-400 mt-1">
                    Deja vacío para mantener la contraseña actual
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de envío (From)
                </label>
                <input
                  type="email"
                  value={smtpFromEmail}
                  onChange={(e) => setSmtpFromEmail(e.target.value)}
                  placeholder="noreply@mirestaurante.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de envío (From)
                </label>
                <input
                  type="text"
                  value={smtpFromName}
                  onChange={(e) => setSmtpFromName(e.target.value)}
                  placeholder="Mi Restaurante"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {smtpMessage && (
              <div
                className={`p-3 rounded-lg ${smtpMessage.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
              >
                {smtpMessage.text}
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              {smtpConfigured && (
                <button
                  type="button"
                  onClick={handleTestSmtp}
                  disabled={smtpTesting}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {smtpTesting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Enviar Email de Prueba
                </button>
              )}
              <button
                type="submit"
                disabled={smtpLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {smtpLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Server className="w-4 h-4" />
                )}
                Guardar Configuración SMTP
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  // Subscription loading check - AFTER all hooks are declared
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  // Feature access check - AFTER all hooks are declared
  if (!canAccessEmailCampaigns && features) {
    return (
      <UpgradeRequired
        feature="emailCampaigns"
        currentPlan={features.planName}
      />
    );
  }

  return (
    <>
      {/* Notifications moved to global toast */}

      {/* SMTP not configured banner */}
      {smtpChecked &&
        !smtpConfigured &&
        user?.role !== "owner" &&
        view === "list" && (
          <div className="mx-3 md:mx-6 mt-3 md:mt-6 mb-0 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
              <Server className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Configura tu servidor SMTP para poder enviar campañas
              </p>
              <p className="text-xs text-amber-600 mt-0.5">
                Necesitas un servidor SMTP (Gmail, Brevo, SendGrid, etc.) para
                enviar emails a tus clientes.
              </p>
            </div>
            <button
              onClick={() => setView("smtp")}
              className="flex-shrink-0 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
            >
              Configurar SMTP
            </button>
          </div>
        )}

      {view === "list" && renderList()}
      {view === "templates" && renderTemplateSelector()}
      {view === "editor" && renderEditor()}
      {view === "stats" && renderStats()}
      {view === "smtp" && renderSmtpConfig()}
    </>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Plantillas predefinidas: devuelven SOLO el fragmento de contenido, ya con
 * los marcadores `<!--rm-content-start/end-->` y los `data-rm-block`
 * necesarios para que `EmailBlockEditor` las abra como bloques editables
 * (Título/Texto/HTML libre/Botón) en vez de un único bloque opaco — nunca un
 * documento completo. El envoltorio —cabecera con el nombre del negocio, pie
 * con {{unsubscribeUrl}}— lo pone siempre `wrapEmailContent()` en
 * `handleSelectTemplate`, el MISMO módulo que envuelve lo que genera la IA:
 * un único envoltorio visual para las dos rutas.
 *
 * Cada plantilla se construye como una lista de `EmailBlock[]` (Texto de
 * saludo → Título → Texto de intro → HTML libre con la "tarjeta" de detalles
 * (menú/evento/horario…, verbatim igual que antes: no hay un tipo de bloque
 * "tarjeta" en el editor) → Texto de cierre opcional → Botón), y
 * `buildBlocksFragment()` (de `EmailBlockEditor.tsx`) las serializa con los
 * marcadores. Mantiene el mismo aspecto visual que las plantillas anteriores
 * (mismos textos, mismo HTML de las tarjetas) pero ahora el título, los
 * textos y el botón (color, enlace, redondeado…) se editan bloque a bloque.
 */

/** Botón de plantilla: mismo estilo que usaban ya `getDefaultTemplate`/
 * `getCampaignTemplate` (sin bordes redondeados — Outlook de escritorio los
 * ignora, ver `BRAND_COLORS`/`email-layout.ts`). */
function tplButton(text: string): EmailBlock {
  return {
    id: "tpl-button",
    type: "button",
    text,
    href: "{{bookingUrl}}",
    bgColor: BRAND_COLORS.primaryDark,
    textColor: "#ffffff",
    align: "center",
    fullWidth: false,
    radius: 0,
  };
}

function tplHeading(text: string): EmailBlock {
  return { id: "tpl-heading", type: "heading", text, size: "m", color: "#111111", align: "left" };
}

function tplText(html: string): EmailBlock {
  return { id: "tpl-text", type: "text", html };
}

function tplHtml(html: string): EmailBlock {
  return { id: "tpl-html", type: "html", html };
}

const TPL_GREETING = `Hola <strong>{{customerName}}</strong>,`;

/** Compone las plantillas "ricas" (todas salvo "blank"): saludo, título,
 * intro, tarjeta de detalles (HTML libre) y botón, con un texto de cierre
 * opcional antes del botón. */
function buildRichTemplate(opts: {
  heading: string;
  intro: string;
  card: string;
  closing?: string;
  buttonText: string;
}): string {
  const blocks: EmailBlock[] = [
    tplText(TPL_GREETING),
    tplHeading(opts.heading),
    tplText(opts.intro),
    tplHtml(opts.card),
    ...(opts.closing ? [tplText(opts.closing)] : []),
    tplButton(opts.buttonText),
  ];
  return buildBlocksFragment(blocks);
}

function getDefaultTemplate(): string {
  return buildBlocksFragment([
    tplHeading("Escribe aquí tu título"),
    tplText(`${TPL_GREETING} escribe aquí el contenido de tu newsletter...`),
    tplButton("Ver más"),
  ]);
}

function getCampaignTemplate(templateId: string): string {
  const templates: Record<string, () => string> = {
    menu_especial: () =>
      buildRichTemplate({
        heading: "🍽️ Nuevo Menú Especial",
        intro: "Hemos preparado un menú especial que no te puedes perder:",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 12px;color:#111111;font-size:16px;font-weight:bold;">Menú Degustación</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🥗 Entrante: Ensalada templada de foie</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🍝 Primero: Risotto de setas y trufa</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🥩 Segundo: Solomillo con reducción de Pedro Ximénez</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🍮 Postre: Coulant de chocolate con helado de vainilla</td></tr>
</table>
<p style="margin:12px 0 0;color:${BRAND_COLORS.primaryDark};font-size:18px;font-weight:bold;text-align:center;">45€ por persona (bebida incluida)</p>
</td></tr>
</table>`,
        closing:
          "Disponible del <strong>[FECHA INICIO]</strong> al <strong>[FECHA FIN]</strong>. Las plazas son limitadas, ¡reserva ahora!",
        buttonText: "Reservar Ahora",
      }),

    evento: () =>
      buildRichTemplate({
        heading: "🎵 Evento Especial",
        intro: "Te invitamos a una noche inolvidable:",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 8px;color:#111111;font-size:16px;font-weight:bold;">🎤 [NOMBRE DEL EVENTO]</p>
<p style="margin:0 0 6px;color:#333333;font-size:14px;"><strong style="color:#111111;">📅 Fecha:</strong> [FECHA]</p>
<p style="margin:0 0 6px;color:#333333;font-size:14px;"><strong style="color:#111111;">🕐 Hora:</strong> [HORA]</p>
<p style="margin:0;color:#333333;font-size:14px;"><strong style="color:#111111;">💰 Precio:</strong> [PRECIO]€ (incluye cena + espectáculo)</p>
</td></tr>
</table>`,
        closing:
          "Ven a disfrutar de una velada con música en vivo, gastronomía de autor y un ambiente único. ¡Las entradas se agotan rápido!",
        buttonText: "Reservar Entrada",
      }),

    horarios: () =>
      buildRichTemplate({
        heading: "🕐 Actualización de Horarios",
        intro: "Queremos informarte de cambios en nuestros horarios:",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 12px;color:#111111;font-size:15px;font-weight:bold;">Nuevos horarios a partir del [FECHA]:</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;border-bottom:1px solid #e5e5e5;"><strong style="color:#111111;">Lunes a Viernes:</strong> 13:00 – 16:00 y 20:00 – 23:30</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;border-bottom:1px solid #e5e5e5;"><strong style="color:#111111;">Sábados:</strong> 13:00 – 16:30 y 20:00 – 00:00</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;"><strong style="color:#111111;">Domingos:</strong> 13:00 – 17:00</td></tr>
</table>
<p style="margin:10px 0 0;color:#555555;font-size:13px;font-style:italic;">Cocina cierra 30 minutos antes.</p>
</td></tr>
</table>`,
        closing: "Ten en cuenta estos cambios para tu próxima visita. ¡Te esperamos!",
        buttonText: "Ver Carta",
      }),

    delivery: () =>
      buildRichTemplate({
        heading: "🛵 ¡Ahora con Servicio a Domicilio!",
        intro: "¡Gran noticia! Ahora puedes disfrutar de nuestros platos desde la comodidad de tu casa.",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 12px;color:#111111;font-size:15px;font-weight:bold;">¿Cómo funciona?</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">📱 Haz tu pedido por teléfono o web</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🍳 Preparamos tu pedido al momento</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🛵 Te lo llevamos a casa en menos de 45 min</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ Envío gratis a partir de 25€</td></tr>
</table>
</td></tr>
</table>`,
        closing: "<strong>Zona de reparto:</strong> Radio de 5 km desde el restaurante.",
        buttonText: "Hacer Pedido",
      }),

    promocion: () =>
      buildRichTemplate({
        heading: "🏷️ ¡Oferta Especial para Ti!",
        intro: "Como cliente especial, tenemos una promoción exclusiva:",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" align="center" style="padding:20px;border-left:3px solid ${BRAND_COLORS.primaryDark};">
<p style="margin:0;color:${BRAND_COLORS.primaryDark};font-size:32px;font-weight:bold;">-20%</p>
<p style="margin:4px 0;color:#111111;font-size:16px;">en tu próxima cena</p>
<p style="margin:8px 0 0;color:#555555;font-size:14px;">Código: <strong style="color:${BRAND_COLORS.primaryDark};">ESPECIAL20</strong></p>
</td></tr>
</table>

<p style="margin:0 0 8px;color:#111111;font-size:15px;font-weight:bold;">Condiciones:</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ Válido hasta el [FECHA]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ Aplicable a la cuenta total (bebidas incluidas)</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ No acumulable con otras promociones</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ Muestra este email al camarero</td></tr>
</table>`,
        buttonText: "Reservar con Descuento",
      }),

    festivo: () =>
      buildRichTemplate({
        heading: "🎉 Menú Especial Festivo",
        intro: "¡Celebra con nosotros! Hemos preparado un menú especial para estas fechas señaladas.",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 4px;color:#111111;font-size:16px;font-weight:bold;">🎊 Menú Festivo</p>
<p style="margin:0 0 12px;color:#555555;font-size:14px;">📅 [FECHA DEL FESTIVO]</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🥂 Aperitivo de bienvenida</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🍽️ Entrantes variados para compartir</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🥩 Plato principal a elegir</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🍮 Postre especial</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🍷 Bebidas incluidas</td></tr>
</table>
<p style="margin:12px 0 0;color:${BRAND_COLORS.primaryDark};font-size:20px;font-weight:bold;text-align:center;">55€ / persona</p>
</td></tr>
</table>`,
        closing: "Las mesas son limitadas. ¡No te quedes sin la tuya!",
        buttonText: "Reservar Mesa",
      }),

    novedades: () =>
      buildRichTemplate({
        heading: "✨ Novedades del Restaurante",
        intro: "Queremos compartir contigo las últimas novedades:",
        card: `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:12px 0;border-bottom:1px solid #e5e5e5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">🆕</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">Nueva Carta de Temporada</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">Hemos renovado nuestra carta con ingredientes de proximidad y recetas de autor.</p>
</td>
</tr></table>
</td></tr>
<tr><td style="padding:12px 0;border-bottom:1px solid #e5e5e5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">🌿</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">Terraza Renovada</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">Nuestra terraza luce un nuevo look. ¡Ven a disfrutarla!</p>
</td>
</tr></table>
</td></tr>
<tr><td style="padding:12px 0;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">👨‍🍳</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">Nuevo Chef Pastelero</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">Se incorpora a nuestro equipo un chef pastelero de renombre internacional.</p>
</td>
</tr></table>
</td></tr>
</table>`,
        closing: "¡Ven a descubrir todas las novedades en persona!",
        buttonText: "Hacer una Reserva",
      }),
  };

  const build = templates[templateId];
  return build ? build() : getDefaultTemplate();
}
